import { vegaArtifactGraph } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useGetConversationArtifactsQuery } from '@gonfalon/observability-graphql/hooks';
import {
  canonicalizeArtifactExternalId,
  canonicalizeArtifactType,
} from '@gonfalon/observability-ui/components/Artifact';

import {
  CONVERSATION_ARTIFACTS_LIMIT,
  CREATED_MODIFIED_RELATIONSHIPS,
  REFERENCED_RELATIONSHIPS,
} from '../../../../internal/conversationArtifacts';

// A single artifact row as returned by the `conversationArtifacts` query. Derived from the
// generated hook so it stays in sync with the schema without an extra type import.
export type ConversationArtifact = NonNullable<
  ReturnType<typeof useGetConversationArtifactsQuery>['data']
>['conversationArtifacts'][number];

export interface ConversationArtifactSection {
  label: string;
  artifacts: ConversationArtifact[];
}

export interface ExcludeArtifact {
  type: string;
  externalId: string;
}

interface Args {
  projectId: string;
  conversationId: string;
  // Soft cap per section. A single conversation rarely produces many artifacts.
  limit?: number;
  // When viewing an artifact's detail page, omit that artifact from nested conversation lists.
  excludeArtifact?: ExcludeArtifact;
}

export interface ConversationArtifactSectionsResult {
  // The `vega-artifact-graph` flag is on.
  enabled: boolean;
  // A saved conversation exists to fetch artifacts for (`new` is the unsaved placeholder).
  isRealConversation: boolean;
  sections: ConversationArtifactSection[];
  // At least one section has an artifact.
  hasAny: boolean;
  // Total artifacts across all sections (drives the Artifacts tab count badge).
  totalCount: number;
  loading: boolean;
  error: boolean;
}

function artifactIdentityKey(type: string, externalId: string): string {
  return `${canonicalizeArtifactType(type)}::${canonicalizeArtifactExternalId(type, externalId)}`;
}

function excludeMatches(artifact: ConversationArtifact, exclude: ExcludeArtifact | undefined): boolean {
  if (!exclude) {
    return false;
  }
  return (
    artifactIdentityKey(artifact.type, artifact.external_id) === artifactIdentityKey(exclude.type, exclude.externalId)
  );
}

/**
 * Conversation-scoped artifact data, grouped into "Created / modified" and "Referenced".
 *
 * Centralizes the query + grouping so the Artifacts tab badge, the hover dropdown, and the
 * Artifacts pane all read from one place instead of duplicating the two-query pattern.
 *
 * The two sections are the two relationship groupings in `conversationArtifacts`, which is also
 * where `status_changed` is explained.
 *
 * Two `conversationArtifacts` queries fire (one per section): the query returns bare
 * `Artifact` rows with no relationship field, so relationship can only be applied as a
 * server-side filter — there's no way to fetch once and bucket client-side without a schema
 * change. Apollo dedupes identical in-flight queries, so calling this hook in more than one
 * place does not multiply network requests.
 */
export function useConversationArtifactSections({
  projectId,
  conversationId,
  limit = CONVERSATION_ARTIFACTS_LIMIT,
  excludeArtifact,
}: Args): ConversationArtifactSectionsResult {
  const enabled = vegaArtifactGraph();
  const isRealConversation = !!conversationId && conversationId !== 'new';
  const skip = !enabled || !isRealConversation || !projectId;

  const {
    data: createdData,
    loading: createdLoading,
    error: createdError,
  } = useGetConversationArtifactsQuery({
    variables: {
      project_id: projectId,
      conversation_id: conversationId,
      relationships: CREATED_MODIFIED_RELATIONSHIPS,
      limit,
    },
    skip,
  });
  const {
    data: referencedData,
    loading: referencedLoading,
    error: referencedError,
  } = useGetConversationArtifactsQuery({
    variables: {
      project_id: projectId,
      conversation_id: conversationId,
      relationships: REFERENCED_RELATIONSHIPS,
      limit,
    },
    skip,
  });

  const created = (createdData?.conversationArtifacts ?? []).filter((a) => !excludeMatches(a, excludeArtifact));
  // An artifact the conversation created or edited was necessarily also referenced, so
  // listing it under "Referenced" is just noise — drop anything already shown above.
  const createdIds = new Set(created.map((a) => a.id));
  const referenced = (referencedData?.conversationArtifacts ?? []).filter(
    (a) => !createdIds.has(a.id) && !excludeMatches(a, excludeArtifact),
  );

  const sections: ConversationArtifactSection[] = [
    { label: 'Created / modified', artifacts: created },
    { label: 'Referenced', artifacts: referenced },
  ];

  const totalCount = created.length + referenced.length;

  return {
    enabled,
    isRealConversation,
    sections,
    hasAny: totalCount > 0,
    totalCount,
    loading: createdLoading || referencedLoading,
    error: Boolean(createdError || referencedError),
  };
}
